/* Admin panel sound notifications — plays a short chime when a new
 * public-booking notification arrives. Uses Web Audio API (no binary
 * asset needed; works offline). Enable/disable preference is stored in
 * localStorage and gated on a user gesture (browser autoplay policies).
 *
 * Idempotency: each notification ID is recorded once in
 * `paydaai_booking_sound_seen_v1` (LRU-trimmed) so repeated polls never
 * re-trigger the chime.
 */

const PREF_KEY = 'paydaai_booking_sound_enabled_v1';
const SEEN_KEY = 'paydaai_booking_sound_seen_v1';
const SEEN_MAX = 200;

// ---- preference ----------------------------------------------------------

export function isSoundEnabled() {
  try {
    return localStorage.getItem(PREF_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSoundEnabled(enabled) {
  try {
    if (enabled) localStorage.setItem(PREF_KEY, '1');
    else localStorage.removeItem(PREF_KEY);
  } catch { /* ignore quota errors */ }
}

// ---- seen-set ------------------------------------------------------------

function loadSeen() {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveSeen(arr) {
  try {
    // Cap to SEEN_MAX so localStorage doesn't grow unboundedly
    const trimmed = arr.slice(-SEEN_MAX);
    localStorage.setItem(SEEN_KEY, JSON.stringify(trimmed));
  } catch { /* ignore */ }
}

export function markSeen(ids) {
  const set = new Set(loadSeen());
  for (const id of ids) if (id) set.add(id);
  saveSeen([...set]);
}

export function hasSeen(id) {
  if (!id) return true;
  return new Set(loadSeen()).has(id);
}

// ---- audio ---------------------------------------------------------------

let _ctx = null;
function audioContext() {
  if (_ctx) return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _ctx = new Ctx();
  return _ctx;
}

/** Synthesise a friendly two-tone chime (no asset file needed). */
export function playChime() {
  const ctx = audioContext();
  if (!ctx) return false;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});

  // Two short sine pulses: 880Hz then 660Hz, gentle attack/release.
  const tone = (freq, startAt, duration = 0.18, peak = 0.18) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const t0 = ctx.currentTime + startAt;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(peak, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.05);
  };

  tone(880, 0);
  tone(660, 0.22);
  return true;
}

// ---- public API ----------------------------------------------------------

/** First call must originate from a user-gesture (button click). It both
 *  unlocks the AudioContext on Safari/iOS AND persists the preference.
 *  Returns true on success; on browsers without Web Audio (rare), the
 *  preference is NOT persisted so the UI stays in the "off" state.
 */
export function enableSoundNotifications() {
  const ok = playChime();
  if (ok) setSoundEnabled(true);
  return ok;
}

export function disableSoundNotifications() {
  setSoundEnabled(false);
}
